import { ArrowDown, ArrowUpRight, Mail, MapPin } from "lucide-react";

/** Selected results, each carrying the context it came from. */
const results = [
  { value: "180ms", label: "P50 inference latency", context: "GPU LLM serving" },
  { value: "175K+", label: "records served in production", context: "Project 990" },
  { value: "500K+", label: "interactions handled", context: "Hyphenova" },
  { value: "0.95", label: "R² on unseen data", context: "ClaimGuard" },
];

const NewHero = () => (
  <section className="relative px-6 md:px-10 pt-16 pb-20 md:pt-24 md:pb-28">
    <div className="mx-auto max-w-[76rem]">
      <div className="grid gap-14 lg:grid-cols-[1fr_18rem] lg:gap-20 items-start">
        {/* ── Left: the statement ── */}
        <div>
          <div
            className="mb-8 flex flex-wrap items-center gap-x-3 gap-y-2 animate-rise"
            style={{ animationDelay: "40ms" }}
          >
            <p className="type-label text-primary">
              Forward Deployed AI Engineer · Nimble Gravity
            </p>
            <span className="text-border" aria-hidden="true">
              ·
            </span>
            <p className="type-label inline-flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-3 w-3" />
              Austin, TX
            </p>
          </div>

          <h1
            className="type-hero text-foreground animate-rise"
            style={{ animationDelay: "80ms" }}
          >
            I build AI that
            <br />
            runs in production.
          </h1>

          <p
            className="type-lead text-muted-foreground max-w-xl mt-8 animate-rise"
            style={{ animationDelay: "140ms" }}
          >
            GPU inference servers, fraud detection pipelines, healthcare ML systems —
            shipped, deployed, and measured against real numbers. If it doesn&rsquo;t run
            in production, it doesn&rsquo;t count.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-wrap items-center gap-x-8 gap-y-4 mt-10 animate-rise"
            style={{ animationDelay: "200ms" }}
          >
            <a
              href="mailto:jeetp5118@gmail.com"
              className="group inline-flex items-center gap-2.5 rounded-md bg-foreground px-5 py-3 text-sm font-medium text-background transition-opacity hover:opacity-88"
            >
              <Mail className="h-4 w-4" />
              Get in touch
            </a>
            <a
              href="https://github.com/Jeet-51"
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              GitHub
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
            <a
              href="https://linkedin.com/in/pateljeet22"
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              LinkedIn
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        {/* ── Right: portrait ── */}
        <div
          className="order-first lg:order-none animate-rise"
          style={{ animationDelay: "120ms" }}
        >
          <div className="relative w-36 lg:w-full">
            <img
              src={`${import.meta.env.BASE_URL}profile.jpeg`}
              alt="Jeet Patel"
              className="w-full aspect-square rounded-md object-cover object-top grayscale-[0.15]"
              loading="eager"
            />
          </div>
          <div className="mt-4 space-y-1">
            <p className="text-sm font-medium text-foreground">Jeet Patel</p>
            <p className="text-sm text-muted-foreground">
              MS Data Science, Indiana University
            </p>
            <p className="text-sm text-muted-foreground/70">Austin, Texas</p>
          </div>
        </div>
      </div>

      {/* ── Results strip — the numbers do the arguing ── */}
      <div
        className="mt-20 md:mt-28 border-t border-border pt-10 animate-rise"
        style={{ animationDelay: "260ms" }}
      >
        <p className="type-label text-muted-foreground/60 mb-8">Selected results</p>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-4">
          {results.map((r) => (
            <div key={r.label}>
              <dd className="type-metric text-foreground text-[clamp(2rem,4.5vw,3.25rem)]">
                {r.value}
              </dd>
              <dt className="mt-3 text-sm leading-snug text-muted-foreground">{r.label}</dt>
              <p className="mt-1.5 type-label text-muted-foreground/45">{r.context}</p>
            </div>
          ))}
        </dl>
      </div>

      {/* Scroll cue */}
      <div className="mt-16 flex justify-center lg:justify-start">
        <a
          href="#experience"
          className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
        >
          <span className="type-label">Experience</span>
          <ArrowDown className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  </section>
);

export default NewHero;
